import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, MapPin, Phone, Mail, Ban, Loader2 } from "lucide-react";
import { COMPANY, SERVICES } from "../data/content";
import { Reveal, Overline } from "./Reveal";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLICIES = [
  { icon: Clock, title: "Working Hours", text: `${COMPANY.hours} · ${COMPANY.closed}` },
  { icon: Ban, title: "No Free Site Visits", text: "Site visits are billed — serious enquiries only. Visitation cost is shared upfront." },
  { icon: MapPin, title: "Office", text: COMPANY.address },
  { icon: Phone, title: "Call / WhatsApp", text: `${COMPANY.phone} · ${COMPANY.founderPhone}` },
  { icon: Mail, title: "Email", text: COMPANY.email },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", service: "General", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We will call you back within working hours.");
      setForm({ name: "", phone: "", service: "General", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-paper py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Overline>Start a project</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Tell us what you want to build.
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="space-y-4" data-testid="contact-policies">
              {POLICIES.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div className="flex gap-4 border border-slate-200 bg-white p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-navy text-white">
                      <p.icon size={17} />
                    </span>
                    <div>
                      <h3 className="font-display text-sm font-bold uppercase tracking-wide text-navy">{p.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{p.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.3}>
              <div className="mt-4 border border-slate-200 bg-white hard-shadow-sm" data-testid="office-map-card">
                <iframe
                  title="Subhankara Design & Construction office location"
                  data-testid="office-map"
                  src="https://www.google.com/maps?q=25.625147,85.1163027&z=17&output=embed"
                  className="h-64 w-full border-0"
                  loading="lazy"
                />
                <a
                  href="https://www.google.com/maps/place/Subhankara+Design+and+Construction+Pvt.+Ltd./@25.625147,85.1137278,17z/data=!3m1!4b1!4m6!3m5!1s0x39ed5909575e624f:0xae93835ccb1ef017!8m2!3d25.625147!4d85.1163027!16s%2Fg%2F11qmmk5fx1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="directions-link"
                  className="flex items-center justify-center gap-2 border-t border-slate-200 bg-navy px-4 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-electric"
                >
                  <MapPin size={14} /> Get Directions
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="enquiry-form"
              className="hard-shadow border border-navy bg-white p-7 sm:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="enquiry-name" className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Your Name *
                  </label>
                  <Input
                    id="enquiry-name"
                    data-testid="enquiry-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full name"
                    className="rounded-none border-slate-300 focus-visible:ring-electric"
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Phone / WhatsApp *
                  </label>
                  <Input
                    id="enquiry-phone"
                    data-testid="enquiry-phone-input"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 ..."
                    className="rounded-none border-slate-300 focus-visible:ring-electric"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="enquiry-service" className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Service Needed
                </label>
                <select
                  id="enquiry-service"
                  data-testid="enquiry-service-select"
                  value={form.service}
                  onChange={set("service")}
                  className="flex h-10 w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-electric"
                >
                  <option>General</option>
                  {SERVICES.map((s) => (
                    <option key={s.id}>{s.title}</option>
                  ))}
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="enquiry-message" className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Project Details *
                </label>
                <Textarea
                  id="enquiry-message"
                  data-testid="enquiry-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Location, plot / carpet area, what you want built or designed..."
                  className="rounded-none border-slate-300 focus-visible:ring-electric"
                />
              </div>

              <button
                type="submit"
                data-testid="enquiry-submit-button"
                disabled={sending}
                className="mt-7 flex w-full items-center justify-center gap-2 bg-electric px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-navy disabled:opacity-60"
              >
                {sending && <Loader2 size={15} className="animate-spin" />}
                {sending ? "Sending..." : "Send Enquiry"}
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                Replies within working hours · 10 AM – 6 PM · Thursdays closed
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
